/**
 * SpeechRecognition utility for the Therapist AI web app
 * Provides a consistent interface for the Web Speech API's speech recognition functionality
 * with fallbacks and cross-browser compatibility, mobile optimizations, and page visibility handling
 */

class SpeechRecognitionService {
  constructor() {
    // Initialize properties
    this.recognition = null;
    this.isListening = false;
    this.isSupported = false;
    this.continuous = false;
    this.interimResults = true;
    this.lang = 'en-US';
    this.isMobile = false;
    this.isMobileChrome = false;
    this.autoSendOnMobile = false;
    this.pageVisibilityEnabled = false;
    this.wasListeningBeforeHidden = false;
    
    // Callbacks
    this.onStartCallback = null;
    this.onEndCallback = null;
    this.onResultCallback = null;
    this.onErrorCallback = null;
    this.onAutoSendCallback = null;
    
    // Initialize the service
    this.initialize();
    this.detectMobile();
    this.setupPageVisibilityHandling();
  }
  
  /**
   * Initialize the speech recognition service with browser detection
   */
  initialize() {
    // Check for browser support
    if ('SpeechRecognition' in window) {
      this.recognition = new window.SpeechRecognition();
      this.isSupported = true;
    } else if ('webkitSpeechRecognition' in window) {
      this.recognition = new window.webkitSpeechRecognition();
      this.isSupported = true;
    } else {
      this.isSupported = false;
      console.warn('Speech recognition is not supported in this browser.');
      return;
    }
    
    // Set default properties
    this.recognition.continuous = this.continuous;
    this.recognition.interimResults = this.interimResults;
    this.recognition.lang = this.lang;
    
    // Set up event handlers
    this.recognition.onstart = () => {
      this.isListening = true;
      if (this.onStartCallback) this.onStartCallback();
    };
    
    this.recognition.onend = () => {
      this.isListening = false;
      if (this.onEndCallback) this.onEndCallback();
    };
    
    this.recognition.onresult = (event) => {
      if (event.results.length > 0) {
        const result = event.results[event.results.length - 1];
        const transcript = result[0].transcript;
        const isFinal = result.isFinal;
        
        if (this.onResultCallback) {
          this.onResultCallback(transcript, isFinal);
        }
        
        // Auto-send on mobile Chrome when final result is received
        if (isFinal && this.autoSendOnMobile && this.isMobileChrome && transcript.trim()) {
          setTimeout(() => {
            if (this.onAutoSendCallback) {
              this.onAutoSendCallback(transcript);
            }
          }, 500); // Small delay to ensure UI updates
        }
      }
    };
    
    this.recognition.onerror = (event) => {
      if (this.onErrorCallback) {
        this.onErrorCallback(event.error);
      }
    };
  }

  /**
   * Detect if running on mobile device and specifically mobile Chrome
   */
  detectMobile() {
    const userAgent = navigator.userAgent;
    
    // Check if mobile device
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    
    // Check if mobile Chrome specifically
    this.isMobileChrome = this.isMobile && /Chrome/i.test(userAgent) && !/Edg|OPR|Samsung/i.test(userAgent);
    
    console.log('🔍 Device detection:', {
      isMobile: this.isMobile,
      isMobileChrome: this.isMobileChrome,
      userAgent: userAgent.substring(0, 100) + '...'
    });
  }

  /**
   * Setup page visibility API to handle tab switching and app switching
   */
  setupPageVisibilityHandling() {
    if (typeof document.visibilityState === 'undefined') {
      console.warn('Page Visibility API not supported');
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Page is hidden (user switched tab, minimized browser, or switched app)
        if (this.isListening && this.pageVisibilityEnabled) {
          console.log('🔇 Page hidden - pausing microphone');
          this.wasListeningBeforeHidden = true;
          this.stop();
        }
      } else if (document.visibilityState === 'visible') {
        // Page is visible again
        if (this.wasListeningBeforeHidden && this.pageVisibilityEnabled) {
          console.log('🔊 Page visible - resuming microphone');
          this.wasListeningBeforeHidden = false;
          // Small delay to ensure proper state restoration
          setTimeout(() => {
            this.start();
          }, 300);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
  }
  
  /**
   * Start speech recognition
   */
  start() {
    if (!this.isSupported) {
      if (this.onErrorCallback) {
        this.onErrorCallback('Speech recognition not supported');
      }
      return;
    }
    
    if (this.isListening) {
      this.stop();
    }
    
    try {
      this.recognition.start();
    } catch (error) {
      if (this.onErrorCallback) {
        this.onErrorCallback(error.message);
      }
    }
  }
  
  /**
   * Stop speech recognition
   */
  stop() {
    if (!this.isSupported || !this.isListening) return;
    
    try {
      this.recognition.stop();
    } catch (error) {
      if (this.onErrorCallback) {
        this.onErrorCallback(error.message);
      }
    }
  }
  
  /**
   * Set whether recognition should be continuous
   * @param {boolean} continuous - Whether recognition should continue after results
   */
  setContinuous(continuous) {
    this.continuous = continuous;
    if (this.recognition) {
      this.recognition.continuous = continuous;
    }
  }
  
  /**
   * Set whether interim results should be returned
   * @param {boolean} interimResults - Whether to return interim results
   */
  setInterimResults(interimResults) {
    this.interimResults = interimResults;
    if (this.recognition) {
      this.recognition.interimResults = interimResults;
    }
  }
  
  /**
   * Set the language for speech recognition
   * @param {string} lang - Language code (e.g., 'en-US')
   */
  setLanguage(lang) {
    this.lang = lang;
    if (this.recognition) {
      this.recognition.lang = lang;
    }
  }
  
  /**
   * Set callback for recognition start
   * @param {Function} callback - Function to call when recognition starts
   */
  onStart(callback) {
    this.onStartCallback = callback;
  }
  
  /**
   * Set callback for recognition end
   * @param {Function} callback - Function to call when recognition ends
   */
  onEnd(callback) {
    this.onEndCallback = callback;
  }
  
  /**
   * Set callback for recognition results
   * @param {Function} callback - Function to call with results (transcript, isFinal)
   */
  onResult(callback) {
    this.onResultCallback = callback;
  }
  
  /**
   * Set callback for recognition errors
   * @param {Function} callback - Function to call with error message
   */
  onError(callback) {
    this.onErrorCallback = callback;
  }
  
  /**
   * Check if speech recognition is supported
   * @returns {boolean} Whether speech recognition is supported
   */
  checkSupport() {
    return this.isSupported;
  }

  /**
   * Enable or disable auto-send functionality on mobile Chrome
   * @param {boolean} enabled - Whether to auto-send messages on mobile Chrome
   */
  setAutoSendOnMobile(enabled) {
    this.autoSendOnMobile = enabled;
  }

  /**
   * Enable or disable page visibility handling for microphone management
   * @param {boolean} enabled - Whether to pause/resume microphone on tab switching
   */
  setPageVisibilityHandling(enabled) {
    this.pageVisibilityEnabled = enabled;
  }

  /**
   * Set callback for auto-send functionality
   * @param {Function} callback - Function to call when auto-sending (receives transcript)
   */
  onAutoSend(callback) {
    this.onAutoSendCallback = callback;
  }

  /**
   * Get device information
   * @returns {object} Device detection results
   */
  getDeviceInfo() {
    return {
      isMobile: this.isMobile,
      isMobileChrome: this.isMobileChrome,
      isSupported: this.isSupported
    };
  }
}

// Export a factory function to create the service
export const createSpeechRecognition = () => {
  return new SpeechRecognitionService();
};
